import React, { useMemo, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../components/ui/table";
import { Button } from '../../../../../components/ui/button';
import { Eye, FileText, Upload, Edit, Filter, X, Loader2, FileBarChart, CheckCircle, QrCode } from 'lucide-react';
import type { NDTData } from '../../../types/index';
import { Input } from '../../../../../components/ui/input';
//import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { SimpleDatePicker } from '../../../../../components/ui/simple-date-picker';
import { useProductionData } from '../../../../../redux/ProductionData/hooks/useProductionData';
import { convertSetupValue } from '../../../../../utils/unitUtils';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { CollapsibleFilterBar } from '../materials/tabs/Filters/CollapsibleFilterBar';
import { DocumentUploadModal } from './DocumentUploadModal';
import {
  updateAssemblyStatus,
  downloadProductionSheetMergedReport,
  clearProductionSheetMergedReportDownload,
  fetchAssemblyData as fetchAssemblyDataAction,
} from '../../../../../redux/ProductionData/actions';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentDownloadModal } from './DocumentDownloadModal';
import { FitmentVisualInspectionModal } from './tabs/FitmentVisualInspectionModal';
import { DocumentApprovalModal } from './DocumentApprovalModal';
import axiosConfig from '../../../../../redux/ProductionData/axiosConfig';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { MediaViewer } from '../../../../../components/viewers/MediaViewer';
import { DocumentViewer } from '../../../../../components/viewers/DocumentViewer';
import { Dialog, DialogContent } from '../../../../../components/ui/dialog';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import { deleteSpecificationDoc } from '../../../../../redux/Project/specificationSaga';
import { DeleteConfirmationDialog } from '../../../../../components/ui/DeleteConfirmationDialog';
import { Pagination } from '../../../../../components/ui/Pagination';

interface AssemblyTableProps {
  selectedPipeSize: number;
  project: any;
  ndtData: NDTData[];
  handleOpenWeldData: (assembly: any, weldIds?: string[]) => void;
  handleNDTUpload: (assemblyNumber: string, file: File) => void;
  setSelectedAssembly: (assembly: string | null) => void;
  // Filter props
  searchQuery?: string;
  statusFilter?: string;
  stageFilter?: string;
  dateRange?: { dateFrom: Date | undefined; dateTo: Date | undefined };
  selectedPipe: any;
  materials: any;
  selectedItem: any;
  canEdit?: boolean;
  canReport?: boolean;
  canSign?: boolean;
  setSelectedItem: (item: any) => void;
  unit: any;
  loadProductionData: boolean;
  specifications?: any;
  isCustomerRepresentativeUser?: boolean;
  isQCInspectorUser?: boolean;
  currentUserTeamMemberId?: string | number;
  isWelderUser?: boolean;
  isAdminOrSuperAdmin?: boolean;
}

// Mock assembly data with more realistic fields for filtering
interface AssemblyData {
  assemblyNumber: string;
  drawing: number;
  wpsNumber: string;
  drawingNumber: string;
  fitmentData: string;
  hasWeldData: boolean;
  visualInspection: string;
  startDate: string;
  endDate: string;
  status: string;
  productionStage: string;

}

export const AssemblyTable: React.FC<AssemblyTableProps> = ({
  selectedPipeSize,
  project,
  ndtData,
  handleOpenWeldData,
  handleNDTUpload,
  setSelectedAssembly,
  selectedPipe,
  materials,
  selectedItem,
  setSelectedItem,
  unit,
  loadProductionData,
  specifications,
  canEdit = true,
  canReport = true,
  canSign = true,
  isCustomerRepresentativeUser = false,
  isQCInspectorUser = false,
  isWelderUser = false,
  currentUserTeamMemberId,
  isAdminOrSuperAdmin = false
}) => {
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ dateFrom: Date | undefined; dateTo: Date | undefined }>({ dateFrom: undefined, dateTo: undefined });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedProductionData, setSelectedProductionData] = useState<any>(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isCoatingUploadModalOpen, setIsCoatingUploadModalOpen] = useState(false);
  const [isCoatingDownloadModalOpen, setIsCoatingDownloadModalOpen] = useState(false);
  const [isWeldingUploadModalOpen, setIsWeldingUploadModalOpen] = useState(false);
  const [isWeldingDownloadModalOpen, setIsWeldingDownloadModalOpen] = useState(false);

  // Modal state for Fitment and Visual Inspection
  const [isFitmentModalOpen, setIsFitmentModalOpen] = useState(false);
  const [isVisualInspectionModalOpen, setIsVisualInspectionModalOpen] = useState(false);
  const [selectedAssemblyForModal, setSelectedAssemblyForModal] = useState<any>(null);
  const [isAlignmentModalOpen, setIsAlignmentModalOpen] = useState(false);
  const [downloadingReportFor, setDownloadingReportFor] = useState<any>(null);
  const [isDocumentApprovalModalOpen, setIsDocumentApprovalModalOpen] = useState(false);
  const [selectedAssemblyForApproval, setSelectedAssemblyForApproval] = useState<any>(null);
  const [isMergeReport, setIsMergeReport] = useState<boolean>(false);
  const [isMergeReportLoading, setIsMergeReportLoading] = useState<boolean>(false);

  // Delete confirmation dialog state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [documentType, setDocumentType] = useState<'NDT' | 'Coating' | 'Welding'>('NDT');


  // Viewer state management
  const [viewerType, setViewerType] = useState<'pdf' | 'media' | 'document' | null>(null);
  const [viewerFile, setViewerFile] = useState<any | null>(null);
  const [viewerFiles, setViewerFiles] = useState<any[]>([]);

  // QR code modal state
  const [isQRCodeModalVisible, setIsQRCodeModalVisible] = useState(false);
  const [selectedAssemblyQRCodes, setSelectedAssemblyQRCodes] = useState<Array<{ text: string; image: string }> | null>(null);
  const [selectedAssemblyNumber, setSelectedAssemblyNumber] = useState<string | null>(null);

  // Redux hooks
  const {
    assemblyData,
    assemblyTotalCount,
    assemblyLoading,
    assemblyError,
    ndtUploadLoading,
    ndtUploadError,
    uploadNDTDocument,
    fetchAssemblyFilters
  } = useProductionData();
  const dispatch = useDispatch();

  const refetchAssemblyList = useCallback(() => {
    if (!selectedPipe?.projectId || !selectedPipe?.pipeId) return;

  //  console.log('refetchAssemblyList', currentPage);
    dispatch(
      fetchAssemblyDataAction({
        projectId: selectedPipe.projectId,
        pipe_id: selectedPipe.pipeId,
        page: currentPage,
        pageSize: itemsPerPage,
        search: debouncedSearch.trim() || undefined,
        sortkey: 'production_id',
        sortOrder: 'ASC',
      })
    );
  }, [
    dispatch,
    selectedPipe?.projectId,
    selectedPipe?.pipeId,
    currentPage,
    itemsPerPage,
    debouncedSearch,
  ]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => window.clearTimeout(id);
  }, [searchQuery]);

  // Reset pagination before passive effects so the fetch effect runs once with page 1 / default page size
  useLayoutEffect(() => {
    setCurrentPage(1);
    setItemsPerPage(10);
  }, [selectedPipe?.pipeId, selectedPipe?.projectId]);

  useLayoutEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);
  // useEffect(() => {
  //   if (!selectedPipe?.pipeId || !selectedPipe?.projectId) {
  //     console.log('loadProductionData', loadProductionData);
  //     return;
  //   }
  //   refetchAssemblyList();
  // },[refetchAssemblyList])

  useEffect(() => {
    if (!loadProductionData &&!selectedPipe?.pipeId || !selectedPipe?.projectId) {
   //   console.log('loadProductionData', loadProductionData);
      return;
    }
    refetchAssemblyList();
  }, [
    loadProductionData,
    refetchAssemblyList,
    selectedPipe?.pipeId,
    selectedPipe?.projectId,
    currentPage,
    itemsPerPage,
    debouncedSearch,
  ]);

  // Get production sheet merged report download state from Redux
  const productionSheetMergedReportDownloadLoading = useSelector((state: any) => state.productionData.productionSheetMergedReportDownloadLoading);
  const productionSheetMergedReportDownloadData = useSelector((state: any) => state.productionData.productionSheetMergedReportDownloadData);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // File type detection utilities
  const getFileExtension = (fileName?: string, fileUrl?: string) => {
    const name = fileName || fileUrl?.split('?')[0].split('/').pop() || '';
    return name.split('.').pop()?.toLowerCase() || '';
  };

  const getViewerType = (file: any): 'pdf' | 'media' | 'document' => {
    const ext = getFileExtension(file?.fileName, file?.fileUrl);
    if (ext === 'pdf') return 'pdf';
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'tiff', 'tif'];
    const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'webm'];
    if (imageExts.includes(ext) || videoExts.includes(ext)) return 'media';
    return 'document'; // Default for office docs and fallback
  };

  // Unified viewer handler
  const openViewer = (fileOrFiles: any | any[]) => {
    if (!fileOrFiles) return;

    // Handle array of files
    if (Array.isArray(fileOrFiles)) {
      const validFiles = fileOrFiles.filter((f: any) => f?.fileUrl || f?.file_url);
      if (validFiles.length === 0) {
        customNotification.error('No valid files to view');
        return;
      }

      // Check if all files are media (images/videos)
      const allMedia = validFiles.every((file: any) => getViewerType(file) === 'media');

      if (allMedia && validFiles.length > 1) {
        // Multiple media files - use MediaViewer
        setViewerFiles(validFiles);
        setViewerType('media');
        setViewerFile(null);
      } else {
        // Single file or mixed types - open first file
        openViewer(validFiles[0]);
      }
      return;
    }

    // Handle single file
    const file = fileOrFiles;
    if (!file?.fileUrl && !file?.file_url) {
      customNotification.error('File URL not available');
      return;
    }

    // Normalize file object to ensure fileUrl exists
    const normalizedFile = {
      ...file,
      fileUrl: file.fileUrl || file.file_url || file.url,
      fileName: file.fileName || file.file_name || file.name
    };

    const type = getViewerType(normalizedFile);
    setViewerType(type);

    if (type === 'media') {
      // For media, use array format for MediaViewer
      setViewerFiles([normalizedFile]);
      setViewerFile(null);
    } else {
      // For PDF and documents, use single file
      setViewerFile(normalizedFile);
      setViewerFiles([]);
    }
  };

  const closeViewer = () => {
    setViewerType(null);
    setViewerFile(null);
    setViewerFiles([]);
  };

  // Modal handlers
  const handleOpenFitmentModal = (assembly: any) => {
    setSelectedAssemblyForModal(assembly);
    setIsFitmentModalOpen(true);
  };

  const handleOpenVisualInspectionModal = (assembly: any) => {
    setSelectedAssemblyForModal(assembly);
    setIsVisualInspectionModalOpen(true);
  };

  const handleCloseFitmentModal = () => {
    setIsFitmentModalOpen(false);
    refetchAssemblyList();
    setSelectedAssemblyForModal(null);
  };

  const handleCloseVisualInspectionModal = () => {
    setIsVisualInspectionModalOpen(false);
    refetchAssemblyList();
    setSelectedAssemblyForModal(null);
  };

  const handleOpenAlignmentModal = (assembly: any) => {
    setSelectedAssemblyForModal(assembly);
    setIsAlignmentModalOpen(true);
  };

  const handleCloseAlignmentModal = () => {
    setIsAlignmentModalOpen(false);
    refetchAssemblyList();
    setSelectedAssemblyForModal(null);
  };

  const handleClearFilters = () => {
    setStatusFilter([]);
    setDateRange({ dateFrom: undefined, dateTo: undefined });
    setSearchQuery('');
  };

  // Calculate hasActiveFilters using useMemo to prevent unnecessary re-renders
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== '' || statusFilter.length > 0 || !!dateRange.dateFrom || !!dateRange.dateTo;
  }, [searchQuery, statusFilter, dateRange]);

  // Update hasActiveFilters when status filter changes
  const handleStatusFilterChange = (values: string[]) => {
    setStatusFilter(values);
  };

  // Update hasActiveFilters when date range changes
  const handleDateRangeChange = (values: { dateFrom: Date | undefined; dateTo: Date | undefined }) => {
    setDateRange(values);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };



  // Handle NDT upload with Redux
  const handleNDTUploadWithRedux = (assemblyNumber: string, file: File) => {
    if (project?.id) {
      uploadNDTDocument({
        projectId: project.id,
        assemblyNumber,
        file
      });
    }
    // Also call the original handler for backward compatibility
    handleNDTUpload(assemblyNumber, file);
  };
  // Apply filters (search is applied server-side via getAllProductions `search` param)
  const filteredAssemblies = useMemo(() => {
    let filtered = assemblyData;
    if (selectedPipe?.numberOfBuckleArrestors) {
      const maxBuckle = selectedPipe.numberOfBuckleArrestors;
      const offset = (currentPage - 1) * itemsPerPage;
      filtered = filtered.filter((_assembly: any, index: number) => offset + index < maxBuckle);
    }

    return filtered.filter((assembly: any) => {
      // Status filter
      if (statusFilter.length > 0) {
        const assemblyStatus = assembly.productionStatus;
        const statusMapping: { [key: string]: string } = {
          'not_started': 'Not Started',
          'in_progress': 'In Progress',
          'completed': 'Completed',
          'on_hold': 'On Hold'
        };

        // Check if the assembly status matches any of the selected filters
        const hasMatchingStatus = statusFilter.some(filterStatus => {
          const mappedStatus = statusMapping[filterStatus];
          return mappedStatus === assemblyStatus;
        });

        if (!hasMatchingStatus) {
          return false;
        }
      }

      // Date range filter
      if (dateRange.dateFrom || dateRange.dateTo) {
        // Check if assembly has a valid start date
        let assemblyStartDate: Date | null = null;

        if (assembly.startDate) {
          // Handle different date formats and validate
          if (typeof assembly.startDate === 'string') {
            const trimmedDate = assembly.startDate.trim();
            if (trimmedDate && trimmedDate !== '') {
              const parsedDate = new Date(trimmedDate);
              if (!isNaN(parsedDate.getTime())) {
                assemblyStartDate = parsedDate;
              }
            }
          } else if (assembly.startDate instanceof Date) {
            if (!isNaN(assembly.startDate.getTime())) {
              assemblyStartDate = assembly.startDate;
            }
          }
        }

        // If assembly has no valid start date, exclude it when date filtering is active
        if (!assemblyStartDate) {
          return false;
        }

        // Normalize dates to start of day for date-only comparison (ignore time)
        // This ensures that only the date (year, month, day) is considered, not the time
        const assemblyDateOnly = new Date(assemblyStartDate.getFullYear(), assemblyStartDate.getMonth(), assemblyStartDate.getDate());

        if (dateRange.dateFrom) {
          const fromDateOnly = new Date(dateRange.dateFrom.getFullYear(), dateRange.dateFrom.getMonth(), dateRange.dateFrom.getDate());

          if (assemblyDateOnly < fromDateOnly) {
            return false;
          }
        }
        if (dateRange.dateTo) {
          const toDateOnly = new Date(dateRange.dateTo.getFullYear(), dateRange.dateTo.getMonth(), dateRange.dateTo.getDate());

          if (assemblyDateOnly > toDateOnly) {
            return false;
          }
        }
      }

      return true;
    });
  }, [assemblyData, selectedPipe, statusFilter, dateRange, currentPage, itemsPerPage]);

  const paginationTotalItems = useMemo(() => {
    const raw = assemblyTotalCount ?? 0;
    if (selectedPipe?.numberOfBuckleArrestors != null) {
      return Math.min(raw, Number(selectedPipe.numberOfBuckleArrestors));
    }
    return raw;
  }, [assemblyTotalCount, selectedPipe?.numberOfBuckleArrestors]);



  const handleUpload = async (response: any) => {
    if (response) {
      if (selectedPipe?.pipeId && selectedProductionData && selectedProductionData?.productionSheet?.productionSheetId) {
        const ndtDocIds = response.map((item: any) => Number(item.docId));
        if (selectedProductionData?.productionSheet?.ndtDoc?.length > 0) {
          const existingNdtDocIds = selectedProductionData.productionSheet.ndtDoc.map((item: any) => item.docId);
          ndtDocIds.push(...existingNdtDocIds);
        }
        const payload = {
          projectId: selectedPipe.projectId,
          pipeId: selectedPipe.pipeId,
          projectTypeId: selectedPipe.projectTypeId,
          productionId: selectedProductionData.productionId,
          productionSheetId: selectedProductionData?.productionSheet?.productionSheetId,
          ndtDocId: ndtDocIds
        };
        // Dispatch the action to save data
        await dispatch(updateAssemblyStatus(payload));


        refetchAssemblyList();

      }
      setIsUploadModalOpen(false);
    }
  };

  const handleDownload = (spec: any) => {
    // Close the modal first
    setIsDownloadModalOpen(false);

    if (spec) {
      // Handle both single file and array of files
      if (Array.isArray(spec) && spec.length > 0) {
        // If array, check if called from modal (single item) or direct (array)
        if (spec.length === 1 && spec[0]?.fileUrl) {
          openViewer(spec[0]);
        } else {
          openViewer(spec);
        }
      } else if (!Array.isArray(spec) && (spec.fileUrl || spec.file_url)) {
        // Single file object (from DocumentDownloadModal)
        openViewer(spec);
      } else if (spec[0] && spec[0].fileUrl) {
        // Legacy format - array with first element
        openViewer(spec[0]);
      } else {
        customNotification.error('No file available to view');
      }
    } else {
      customNotification.error('No file available to view');
    }
  };

  const handleCoatingUpload = async (response: any) => {
    if (response) {
      if (selectedPipe?.pipeId && selectedProductionData && selectedProductionData?.productionSheet?.productionSheetId) {
        const coatingDocIds = response.map((item: any) => Number(item.docId));
        if (selectedProductionData?.productionSheet?.coatingDoc?.length > 0) {
          const existingCoatingDocIds = selectedProductionData.productionSheet.coatingDoc.map((item: any) => item.docId);
          coatingDocIds.push(...existingCoatingDocIds);
        }
        const payload = {
          projectId: selectedPipe.projectId,
          pipeId: selectedPipe.pipeId,
          projectTypeId: selectedPipe.projectTypeId,
          productionId: selectedProductionData.productionId,
          productionSheetId: selectedProductionData?.productionSheet?.productionSheetId,
          coatingDocId: coatingDocIds
        };
        // Dispatch the action to save data
        await dispatch(updateAssemblyStatus(payload));

        refetchAssemblyList();

      }
      setIsCoatingUploadModalOpen(false);
    }
  };

  const handleCoatingDownload = (spec: any) => {
    // Close the modal first
    setIsCoatingDownloadModalOpen(false);

    if (spec) {
      // Handle both single file and array of files
      if (Array.isArray(spec) && spec.length > 0) {
        openViewer(spec);
      } else if (!Array.isArray(spec) && (spec.fileUrl || spec.file_url)) {
        // Single file object
        openViewer(spec);
      } else {
        customNotification.error('No file available to view');
      }
    } else {
      customNotification.error('No file available to view');
    }
  };

  const handleWeldingUpload = async (response: any) => {
    if (response) {
      if (selectedPipe?.pipeId && selectedProductionData && selectedProductionData?.productionSheet?.productionSheetId) {
        const weldingDocIds = response.map((item: any) => Number(item.docId));
        if (selectedProductionData?.productionSheet?.weldingDoc?.length > 0) {
          const existingWeldingDocIds = selectedProductionData.productionSheet.weldingDoc.map((item: any) => item.docId);
          weldingDocIds.push(...existingWeldingDocIds);
        }
        const payload = {
          projectId: selectedPipe.projectId,
          pipeId: selectedPipe.pipeId,
          projectTypeId: selectedPipe.projectTypeId,
          productionId: selectedProductionData.productionId,
          productionSheetId: selectedProductionData?.productionSheet?.productionSheetId,
          weldingDocId: weldingDocIds
        };
        await dispatch(updateAssemblyStatus(payload));

        refetchAssemblyList();
      }
      setIsWeldingUploadModalOpen(false);
    }
  };

  const handleWeldingDownload = (spec: any) => {
    setIsWeldingDownloadModalOpen(false);

    if (spec) {
      if (Array.isArray(spec) && spec.length > 0) {
        openViewer(spec);
      } else if (!Array.isArray(spec) && (spec.fileUrl || spec.file_url)) {
        openViewer(spec);
      } else {
        customNotification.error('No file available to view');
      }
    } else {
      customNotification.error('No file available to view');
    }
  };

  // Handle delete document
  const handleDeleteDocument = (doc: any, type: 'NDT' | 'Coating' | 'Welding') => {
    setDocumentToDelete(doc);
    setDocumentType(type);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete || !selectedProductionData) return;

    setDeleteLoading(true);
    try {
      // Get docId from the document
      const docId = documentToDelete.docId || documentToDelete.doc_id || documentToDelete.id;

      if (!docId) {
        customNotification.error('Failed to delete document', 'Document ID not found');
        setDeleteLoading(false);
        return;
      }

      const docIds = [Number(docId)];
      const typeName = documentType === 'NDT' ? 'NDT' : documentType === 'Coating' ? 'Coating' : 'Welding';

      // Call delete API
      const deleteResponse = await deleteSpecificationDoc(
        docIds,
        documentToDelete,
        typeName
      );

      if (deleteResponse) {
        // Update production assembly data by removing the deleted docId
        if (selectedPipe?.pipeId && selectedProductionData?.productionSheet?.productionSheetId) {
          if (documentType === 'NDT') {
            // Remove deleted docId from ndtDocId array
            const currentNdtDocIds = selectedProductionData.productionSheet.ndtDoc
              ?.map((item: any) => item.docId)
              .filter((id: number) => id !== docId) || [];

            const payload = {
              projectId: selectedPipe.projectId,
              pipeId: selectedPipe.pipeId,
              projectTypeId: selectedPipe.projectTypeId,
              productionId: selectedProductionData.productionId,
              productionSheetId: selectedProductionData?.productionSheet?.productionSheetId,
              ndtDocId: currentNdtDocIds
            };

            await dispatch(updateAssemblyStatus(payload));
          } else if (documentType === 'Coating') {
            // Remove deleted docId from coatingDocId array
            const currentCoatingDocIds = selectedProductionData.productionSheet.coatingDoc
              ?.map((item: any) => item.docId)
              .filter((id: number) => id !== docId) || [];

            const payload = {
              projectId: selectedPipe.projectId,
              pipeId: selectedPipe.pipeId,
              projectTypeId: selectedPipe.projectTypeId,
              productionId: selectedProductionData.productionId,
              productionSheetId: selectedProductionData?.productionSheet?.productionSheetId,
              coatingDocId: currentCoatingDocIds
            };

            await dispatch(updateAssemblyStatus(payload));
          } else if (documentType === 'Welding') {
            // Remove deleted docId from weldingDocId array
            const currentWeldingDocIds = selectedProductionData.productionSheet.weldingDoc
              ?.map((item: any) => item.docId)
              .filter((id: number) => id !== docId) || [];

            const payload = {
              projectId: selectedPipe.projectId,
              pipeId: selectedPipe.pipeId,
              projectTypeId: selectedPipe.projectTypeId,
              productionId: selectedProductionData.productionId,
              productionSheetId: selectedProductionData?.productionSheet?.productionSheetId,
              weldingDocId: currentWeldingDocIds
            };

            await dispatch(updateAssemblyStatus(payload));
          }

          // Refresh assembly data
          refetchAssemblyList();

          // Close modals
          setIsDeleteModalOpen(false);
          setDocumentToDelete(null);
          if (documentType === 'NDT') {
            setIsDownloadModalOpen(false);
          } else if (documentType === 'Coating') {
            setIsCoatingDownloadModalOpen(false);
          } else {
            setIsWeldingDownloadModalOpen(false);
          }
        }
      }
    } catch (error: any) {
      console.error('Error deleting document:', error);
      customNotification.error('Failed to delete document', error?.message || 'An error occurred');
    } finally {
      setDeleteLoading(false);
    }
  };


  const getReadingStatusClass = (productionSheet: any) => {
    if (productionSheet?.productionWelds?.length > 0) {
      // Check if all welds have fitment data and their reading status
      const welds = productionSheet.productionWelds;

      // Helper function to get reading status from weld (check multiple possible paths)
      const getReadingStatus = (weld: any) => {
        return weld?.weldFitment?.details?.isReading ||
          weld?.fitmentState?.details?.isReading ||
          null;
      };

      const readingStatuses = welds.map((weld: any) => getReadingStatus(weld)).filter(Boolean);

      if (readingStatuses.length === 0) {
        // No reading status set for any weld
        return 'bg-page-bg text-heritage-grey border border-[#E2E5EA] hover:bg-page-bg';
      }

      const hasAllAccept = readingStatuses.every((status: string) => status === 'accept');
      const hasAnyReject = readingStatuses.some((status: string) => status === 'reject');
      const hasAnyAccept = readingStatuses.some((status: string) => status === 'accept');

      // If all welds are accept, show green
      if (hasAllAccept && !hasAnyReject) {
        return 'text-galvanizing-green bg-[rgba(31,154,58,0.08)] border hover:bg-green-200';
      }
      // If any weld is reject, show red
      if (hasAnyReject) {
        return 'text-readable-red bg-[rgba(196,0,31,0.06)] border hover:bg-red-200';
      }
      // If some are accept but not all, show yellow/orange
      if (hasAnyAccept) {
        return 'text-[#B38600] bg-[rgba(230,161,0,0.08)] border hover:bg-yellow-200';
      }
      // Default: no reading status set
      return 'bg-page-bg text-heritage-grey border border-[#E2E5EA] hover:bg-page-bg';
    } else {
      return 'text-blueprint-blue bg-[rgba(0,39,63,0.06)] border hover:bg-blue-200';
    }
  };

  // Handle download production sheet merged report
  const handleDownloadProductionSheetMergedReport = async (assembly: any) => {

    if (isMergeReport) {
      return;
    }

    // Validate required data
    if (!selectedPipe?.projectId || !selectedPipe?.pipeId || !assembly?.productionId) {
      customNotification.error('Missing required data to generate report');
      return;
    }

    const payload = {
      projectId: selectedPipe.projectId,
      pipeId: selectedPipe.pipeId,
      productionId: assembly.productionId
    };

    // // Dispatch action to set loading state (saga will show the toast)
    // dispatch(downloadProductionSheetMergedReport(payload));

    // Call API directly and handle download in .then()
    try {
      const params = new URLSearchParams({
        projectId: payload.projectId.toString(),
        pipeId: payload.pipeId.toString(),
        productionId: payload.productionId.toString()
      });

      const url = `/download-proxy/reports/production-sheet-merged?${params.toString()}`;
      //const url = `/reports/production-sheet-merged?${params.toString()}`;
      const res = await axiosConfig.get(url);

      // Handle 202 (Accepted) - report generation in progress
      if (res.status === 202) {
        // Set downloading state for this specific assembly
        setIsMergeReportLoading(true);
        // Set downloading state for this specific assembly

        setDownloadingReportFor(assembly);

        customNotification.success('Report generation in progress!');
        //  console.log('Report generated successfully!');
        setTimeout(() => {
          //  console.log('Report generated successfully! timeout');
          setIsMergeReportLoading(false);
          setDownloadingReportFor(null);
          refetchAssemblyList();
        }, 5000);
        // Don't show error, report generation is in progress
        // The saga already showed the info toast
      } else if (res.status === 200) {

        setIsMergeReportLoading(true);
        // Set downloading state for this specific assembly

        setDownloadingReportFor(assembly);

        customNotification.success('Report generated successfully!');
        //  console.log('Report generated successfully!');

        setIsMergeReportLoading(false);
        setDownloadingReportFor(null);
        refetchAssemblyList();

        // Refresh assembly data to get updated reportDoc

      } else {
        setIsMergeReportLoading(false);
        setDownloadingReportFor(null);
        // Only show error for actual failures (not 202)
        customNotification.error('Failed to generate report');
      }
    } catch (error: any) {
      // Don't show error toast for 202 responses
      if (error.response && error.response.status === 202) {
        // 202 is expected - report generation in progress
      } else {
        customNotification.error('Error downloading report. Please try again.');
      }
    } finally {
      // Clear download state

      dispatch(clearProductionSheetMergedReportDownload());
    }
  };

  // Handle opening reportingDoc file
  const handleOpenReportingDoc = (reportingDoc: any) => {
    if (reportingDoc && reportingDoc.fileUrl) {
      window.open(reportingDoc.fileUrl, '_blank');
    } else {
      customNotification.error('Report file not available');
    }
  };



  // Helper function to transform documents for the modal
  const transformDocumentsForDownload = (documents: any[]) => {
    return documents;
  };

  return (
    <>
      {assemblyLoading ? (
        <div className="flex items-center justify-center min-h-[700px]">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red"></div>
            <span className="text-heritage-grey text-lg">Loading assembly data...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <CollapsibleFilterBar
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              searchPlaceholder="Search assembly number, WPS number, drawing number..."
              activeFilterCount={statusFilter.length + (dateRange.dateFrom || dateRange.dateTo ? 1 : 0)}
              filteredCount={filteredAssemblies.length}
              filteredCountLabel="assemblies"
              onResetFilters={handleClearFilters}
            >
              <MultiSelect
                options={['not_started', 'in_progress', 'completed', 'on_hold'].map(status => ({
                  value: status,
                  label: status === 'not_started' ? 'Not Started' :
                    status === 'in_progress' ? 'In Progress' :
                      status === 'completed' ? 'Completed' :
                        status === 'on_hold' ? 'On Hold' : status
                }))}
                selected={statusFilter}
                onChange={handleStatusFilterChange}
                placeholder="Status"
                isSearchable={false}
              />
              <SimpleDatePicker
                initialDateFrom={dateRange.dateFrom}
                initialDateTo={dateRange.dateTo}
                onUpdate={handleDateRangeChange}
              />
            </CollapsibleFilterBar>
          </div>
          {assemblyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blueprint-blue" />
              <span className="ml-2 text-heritage-grey">Loading assembly data...</span>
            </div>
          ) : assemblyError ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-hero-red">Error loading assembly data: {assemblyError}</span>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)] [scrollbar-gutter:stable]">
                <Table className="min-w-[1200px]" containerClassName="">
                  <TableHeader className="sticky top-0 z-20 bg-white">
                    <TableRow>
                      <TableHead className="text-center w-[120px] whitespace-nowrap">Assembly Number</TableHead>

                      <TableHead className="text-center w-[100px] whitespace-nowrap">WPS Number</TableHead>
                      <TableHead className="text-center w-[120px] whitespace-nowrap">Drawing Number</TableHead>
                      {/* <TableHead className="text-center w-[140px] whitespace-nowrap">Distance to Forge ({unit})</TableHead> */}

                      <TableHead className="text-center w-[80px] whitespace-nowrap">Weld Data</TableHead>
                      <TableHead className="text-center w-[120px] whitespace-nowrap">Alignment</TableHead>
                      <TableHead className="text-center w-[120px] whitespace-nowrap">Fitment</TableHead>
                      <TableHead className="text-center w-[120px] whitespace-nowrap">Visual Inspection</TableHead>
                      {selectedPipe && selectedPipe.coating && <TableHead className="text-center w-[120px] whitespace-nowrap">Coating</TableHead>}
                      {selectedPipe && selectedPipe.NDT && <TableHead className="text-center w-[80px] whitespace-nowrap">NDT</TableHead>}
                      {selectedPipe && selectedPipe.isWeldingEnabled && <TableHead className="text-center w-[80px] whitespace-nowrap">Welding</TableHead>}
                      <TableHead className="text-center w-[100px] whitespace-nowrap">Start Date</TableHead>
                      <TableHead className="text-center w-[100px] whitespace-nowrap">End Date</TableHead>
                      <TableHead className="text-center w-[80px] whitespace-nowrap">Status</TableHead>
                      <TableHead className="text-center w-[100px] whitespace-nowrap">Report & Approval</TableHead>

                      <TableHead className="text-center w-[80px] whitespace-nowrap sticky right-0 bg-white">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssemblies.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={selectedPipe?.coating && selectedPipe?.NDT && selectedPipe?.welding ? 17 : selectedPipe?.coating && selectedPipe?.NDT ? 16 : selectedPipe?.coating || selectedPipe?.NDT || selectedPipe?.welding ? 15 : 14} className="text-center py-8 text-heritage-grey">
                          No assemblies found matching the current filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAssemblies.map((assembly: any) => {
                        const isLaoding = downloadingReportFor && downloadingReportFor.productionId === assembly.productionId && isMergeReportLoading || false
                        return (
                          <TableRow key={assembly.productionId ?? assembly.assemblyNumber}>
                            <TableCell className="text-center w-[120px] whitespace-nowrap">
                              <div className="flex items-center justify-center gap-2">
                                <span>{assembly.assemblyNumber}</span>
                                {/* {assembly.qrCodes && assembly.qrCodes.length > 0 && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedAssemblyQRCodes(assembly.qrCodes);
                                    setSelectedAssemblyNumber(assembly.assemblyNumber);
                                    setIsQRCodeModalVisible(true);
                                  }}
                                  className="flex items-center justify-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] p-1 h-6 w-6"
                                  title="View QR Codes"
                                >
                                  <QrCode className="h-3 w-3" />
                                </Button>
                              )} */}
                              </div>
                            </TableCell>

                            <TableCell className="text-center w-[100px] whitespace-nowrap">
                              {assembly.productionSheet?.wpsDoc?.fileName || '-'}
                            </TableCell>
                            <TableCell className="text-center w-[120px] whitespace-nowrap">
                              {assembly.productionSheet?.drawingDoc?.fileName || '-'}
                            </TableCell>
                            {/* <TableCell className="text-center w-[140px] whitespace-nowrap">
                            {assembly.productionSheet?.distanceFromPipeEndToForge ?
                              convertSetupValue(assembly.productionSheet.distanceFromPipeEndToForge) : '-'}
                          </TableCell> */}

                            <TableCell className="text-center w-[80px]">
                              {assembly.productionSheet && assembly?.productionSheet?.productionSheetId &&
                                assembly?.productionSheet?.productionWelds && assembly?.productionSheet?.productionWelds?.length > 0 ? (
                                <div className="flex items-center justify-center">
                                  <Button
                                    variant="outline"
                                    onClick={() => {
                                      // Extract weld IDs from productionWelds
                                      const weldIds = assembly?.productionSheet?.productionWelds?.map((weld: any) => weld.weldName).filter(Boolean) || [];
                                      handleOpenWeldData(assembly, weldIds);
                                    }}
                                    className="flex items-center justify-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] mx-auto"    >
                                    <Eye className="h-4 w-4" />
                                    {/* <span className="text-xs">({assembly?.productionSheet?.productionWelds?.length} welds)</span> */}
                                  </Button>
                                </div>
                              ) : '-'}
                            </TableCell>

                            <TableCell className="text-center w-[120px] whitespace-nowrap">
                              {assembly.productionSheet?.productionSheetId ?
                                <Button
                                  onClick={() => handleOpenAlignmentModal(assembly)}
                                  className="flex items-center justify-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] mx-auto"
                                  title="View Alignment Data"
                                  variant="outline"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                : '-'}
                            </TableCell>
                            <TableCell className="text-center w-[120px] whitespace-nowrap">
                              {assembly.productionSheet?.productionSheetId ?
                                <Button
                                  onClick={() => handleOpenFitmentModal(assembly)}
                                  className={`flex items-center justify-center gap-1 mx-auto
                                  
                                  ${getReadingStatusClass(assembly.productionSheet)}`}
                                  title="View Fitment Data"
                                  variant="outline"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                : '-'}
                            </TableCell>
                            <TableCell className="text-center w-[120px] whitespace-nowrap">
                              {assembly.productionSheet?.productionSheetId ?
                                <Button
                                  disabled={assembly?.productionSheet?.productionSheetId ? false : true}
                                  onClick={() => handleOpenVisualInspectionModal(assembly)}
                                  className="flex items-center justify-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] mx-auto"
                                  title="View Visual Inspection"
                                  variant="outline"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                : '-'}
                            </TableCell>
                            {selectedPipe && selectedPipe.coating && (
                              <TableCell className="text-center w-[80px]">
                                <div className={`flex items-center gap-2 ${!assembly.productionSheet?.coatingDoc || assembly.productionSheet?.coatingDoc.length === 0 ? 'justify-center' : ''}`}>
                                  {assembly.productionSheet?.coatingDoc && assembly.productionSheet?.coatingDoc.length > 0 && (
                                    <Button
                                      title={assembly?.productionSheet?.productionSheetId ? "View Coating Document" : "Upload Coating Document"}
                                      onClick={() => {
                                        setSelectedProductionData(assembly);
                                        if (assembly.productionSheet?.coatingDoc?.length > 0) {
                                          setIsCoatingDownloadModalOpen(true);
                                        } else handleCoatingDownload(assembly.productionSheet?.coatingDoc);
                                      }}
                                      variant="outline"
                                      className="flex items-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                                    >
                                      <FileText className="h-4 w-4" />
                                      {assembly.productionSheet?.coatingDoc.length > 1 ?
                                        assembly.productionSheet?.coatingDoc?.length : ''}
                                    </Button>
                                  )}
                                  <Button
                                    type="button"
                                    className="flex items-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                                    title={assembly?.productionSheet?.productionSheetId ? "Upload Coating Document" : "Create production sheet first"}
                                    variant="outline"
                                    disabled={!canEdit || !assembly?.productionSheet?.productionSheetId}
                                    onClick={() => {
                                      if (assembly?.productionSheet?.productionSheetId) {
                                        setSelectedProductionData(assembly);
                                        setIsCoatingUploadModalOpen(true);
                                      }
                                    }}
                                  >
                                    <Upload className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                            {selectedPipe && selectedPipe.NDT && (
                              <TableCell className="text-center w-[80px]">
                                <div className={`flex items-center gap-2 ${!assembly.productionSheet?.ndtDoc || assembly.productionSheet?.ndtDoc.length === 0 ? 'justify-center' : ''}`}>
                                  {assembly.productionSheet?.ndtDoc && assembly.productionSheet?.ndtDoc.length > 0 && (
                                    <Button

                                      title={assembly?.productionSheet?.productionSheetId ? "View NDT Document" : "Upload NDT Document"}
                                      onClick={() => {
                                        setSelectedProductionData(assembly);
                                        if (assembly.productionSheet?.ndtDoc?.length > 0) {
                                          setIsDownloadModalOpen(true);
                                        } else handleDownload(assembly.productionSheet?.ndtDoc);
                                      }}
                                      variant="outline"
                                      className="flex items-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                                    >
                                      <FileText className="h-4 w-4" />
                                      {assembly.productionSheet?.ndtDoc.length > 1 ?
                                        assembly.productionSheet?.ndtDoc?.length : ''}
                                    </Button>
                                  )}
                                  <Button
                                    type="button"
                                    className="flex items-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                                    title={assembly?.productionSheet?.productionSheetId ? "Upload NDT Document" : "Create production sheet first"}
                                    variant="outline"
                                    disabled={!canEdit || !assembly?.productionSheet?.productionSheetId}
                                    onClick={() => {
                                      if (assembly?.productionSheet?.productionSheetId) {
                                        setSelectedProductionData(assembly);
                                        // console.log('assembly', assembly);
                                        setIsUploadModalOpen(true);
                                      }
                                    }}
                                  >
                                    <Upload className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                            {selectedPipe && selectedPipe.isWeldingEnabled && (
                              <TableCell className="text-center w-[80px]">
                                <div className={`flex items-center gap-2 ${!assembly.productionSheet?.weldingDoc || assembly.productionSheet?.weldingDoc.length === 0 ? 'justify-center' : ''}`}>
                                  {assembly.productionSheet?.weldingDoc && assembly.productionSheet?.weldingDoc.length > 0 && (
                                    <Button
                                      title={assembly?.productionSheet?.productionSheetId ? "View Welding Document" : "Upload Welding Document"}
                                      onClick={() => {
                                        setSelectedProductionData(assembly);
                                        if (assembly.productionSheet?.weldingDoc?.length > 0) {
                                          setIsWeldingDownloadModalOpen(true);
                                        } else handleWeldingDownload(assembly.productionSheet?.weldingDoc);
                                      }}
                                      variant="outline"
                                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                    >
                                      <FileText className="h-4 w-4" />
                                      {assembly.productionSheet?.weldingDoc.length > 1 ?
                                        assembly.productionSheet?.weldingDoc?.length : ''}
                                    </Button>
                                  )}
                                  <Button
                                    type="button"
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                    title={assembly?.productionSheet?.productionSheetId ? "Upload Welding Document" : "Create production sheet first"}
                                    variant="outline"
                                    disabled={!canEdit || !assembly?.productionSheet?.productionSheetId}
                                    onClick={() => {
                                      if (assembly?.productionSheet?.productionSheetId) {
                                        setSelectedProductionData(assembly);
                                        setIsWeldingUploadModalOpen(true);
                                      }
                                    }}
                                  >
                                    <Upload className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}

                            <TableCell className="text-center w-[100px] whitespace-nowrap">
                              {assembly.productionSheet?.startDate ? formatDate(assembly.productionSheet.startDate) : '-'}
                            </TableCell>
                            <TableCell className="text-center w-[100px] whitespace-nowrap">
                              {assembly.productionSheet?.endDate ? formatDate(assembly.productionSheet.endDate) : '-'}
                            </TableCell>
                            <TableCell className="text-center w-[80px] whitespace-nowrap">
                              <div className="flex justify-center">
                                <span className={`px-2 py-1 text-xs rounded-full ${assembly.productionStatus === 'Completed' ? 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]' :
                                  assembly.productionStatus === 'In Progress' ? 'text-[#B38600] bg-[rgba(230,161,0,0.08)]' :
                                    assembly.productionStatus === 'On Hold' ? 'text-readable-red bg-[rgba(196,0,31,0.06)]' :
                                      'bg-page-bg text-text-grey'
                                  }`}>
                                  {assembly.productionStatus}
                                </span>
                              </div>
                            </TableCell>

                            <TableCell className="text-center w-[100px] whitespace-nowrap">
                              <div className={`flex items-center gap-2 justify-center`}>


                                {assembly.productionSheet?.productionSheetId && <Button
                                  onClick={() => {
                                    setIsMergeReport(false);
                                    setSelectedAssemblyForApproval(assembly);
                                    setIsDocumentApprovalModalOpen(true);
                                  }}
                                  className="flex items-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)]"
                                  title="Report Approval for Assembly"
                                  variant="outline"
                                  disabled={!canReport || isLaoding}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>}
                                {assembly.productionSheet?.productionSheetId && <Button
                                  onClick={() => {
                                    setIsMergeReport(true);
                                    setSelectedAssemblyForApproval(assembly);
                                    setIsDocumentApprovalModalOpen(true);
                                  }}
                                  className="flex items-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)]"
                                  title="Report Approval for Assembly"
                                  variant="outline"
                                  disabled={!canReport || isLaoding}
                                >
                                  <FileBarChart className="h-4 w-4" />
                                </Button>}



                                {!assembly.productionSheet?.productionSheetId && '-'}
                              </div>
                            </TableCell>


                            <TableCell className="text-center w-[80px] whitespace-nowrap sticky right-0 bg-white">
                              <div className="flex justify-center">

                                <Button
                                  onClick={() => {
                                    setSelectedAssembly(assembly.assemblyNumber);
                                    setSelectedItem(assembly);
                                  }}
                                  className="flex items-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)]"
                                  title="Edit Assembly"
                                  variant="outline"
                                // disabled={!canEdit}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
            {!assemblyLoading && paginationTotalItems > 10 && (
              <div className="pl-3 pr-2 sm:pl-4">
                <Pagination
                  currentPage={currentPage}
                  totalItems={paginationTotalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={setItemsPerPage}
                  pageSizeOptions={[10, 20, 50, 100]}
                />
              </div>
            )}
            </>
          )}
        </>
      )}

      {/* Document Upload Modal */}
      {selectedProductionData && isUploadModalOpen && <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
        title={`Upload NDT Document`}
        acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
        multiple={true}
        maxFiles={100}
        buttonText="Select Document"
        label="Document"
        description="Upload specification documents (PDF, Excel, DOC, etc...)"
        spec={selectedProductionData}
        documentType="NDT"
      />
      }
      {/* Document Download Modal - Only show when there are multiple files */}
      {selectedProductionData && selectedProductionData.productionSheet?.ndtDoc && (
        <DocumentDownloadModal
          isOpen={isDownloadModalOpen}
          onClose={() => setIsDownloadModalOpen(false)}
          documents={selectedProductionData?.productionSheet?.ndtDoc}
          onDownload={handleDownload}
          onDelete={(doc) => handleDeleteDocument(doc, 'NDT')}
          title={`Download NDT Document`}
          canDelete={canEdit}
        />
      )}

      {/* Coating Document Upload Modal */}
      {selectedProductionData && isCoatingUploadModalOpen && <DocumentUploadModal
        isOpen={isCoatingUploadModalOpen}
        onClose={() => setIsCoatingUploadModalOpen(false)}
        onUpload={handleCoatingUpload}
        title={`Upload Coating Document`}
        acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
        multiple={true}
        maxFiles={100}
        buttonText="Select Document"
        label="Document"
        description="Upload coating documents (PDF, Excel, DOC, etc...)"
        spec={selectedProductionData}
        documentType="Coating"
      />
      }
      {/* Coating Document Download Modal - Only show when there are multiple files */}
      {selectedProductionData && selectedProductionData.productionSheet?.coatingDoc && (
        <DocumentDownloadModal
          isOpen={isCoatingDownloadModalOpen}
          onClose={() => setIsCoatingDownloadModalOpen(false)}
          documents={transformDocumentsForDownload(selectedProductionData?.productionSheet?.coatingDoc)}
          onDownload={handleCoatingDownload}
          onDelete={(doc) => handleDeleteDocument(doc, 'Coating')}
          title={`Download Coating Document`}
          canDelete={canEdit}
        />
      )}

      {/* Welding Document Upload Modal */}
      {selectedProductionData && isWeldingUploadModalOpen && <DocumentUploadModal
        isOpen={isWeldingUploadModalOpen}
        onClose={() => setIsWeldingUploadModalOpen(false)}
        onUpload={handleWeldingUpload}
        title={`Upload Welding Document`}
        acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
        multiple={true}
        maxFiles={100}
        buttonText="Select Document"
        label="Document"
        description="Upload welding documents (PDF, Excel, DOC, etc...)"
        spec={selectedProductionData}
        documentType="Welding"
      />
      }
      {/* Welding Document Download Modal */}
      {selectedProductionData && selectedProductionData.productionSheet?.weldingDoc && (
        <DocumentDownloadModal
          isOpen={isWeldingDownloadModalOpen}
          onClose={() => setIsWeldingDownloadModalOpen(false)}
          documents={transformDocumentsForDownload(selectedProductionData?.productionSheet?.weldingDoc)}
          onDownload={handleWeldingDownload}
          onDelete={(doc) => handleDeleteDocument(doc, 'Welding')}
          title={`Download Welding Document`}
          canDelete={canEdit}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isLoading={deleteLoading}
        itemToDelete={documentToDelete}
        itemType={documentType === 'NDT' ? 'NDT Document' : documentType === 'Coating' ? 'Coating Document' : 'Welding Document'}
        itemName={documentToDelete?.fileName || documentToDelete?.file_name || ''}
        title={`Delete ${documentType} Document`}
      />

      {/* Fitment Modal */}
      {isFitmentModalOpen && (
        <FitmentVisualInspectionModal
          isOpen={isFitmentModalOpen}
          onClose={handleCloseFitmentModal}
          selectedItem={selectedAssemblyForModal}
          selectedPipe={selectedPipe}
          materials={materials}
          productionSheetData={selectedAssemblyForModal.productionSheet}
          type="fitment"
          canEdit={canEdit}
          canReport={canReport}
          canSign={canSign}
          isCustomerRepresentativeUser={isCustomerRepresentativeUser}
          isQCInspectorUser={isQCInspectorUser}
          currentUserTeamMemberId={currentUserTeamMemberId}
          isAdminOrSuperAdmin={isAdminOrSuperAdmin}
        />
      )}

      {/* Visual Inspection Modal */}
      {isVisualInspectionModalOpen && (
        <FitmentVisualInspectionModal
          isOpen={isVisualInspectionModalOpen}
          onClose={handleCloseVisualInspectionModal}
          selectedItem={selectedAssemblyForModal}
          selectedPipe={selectedPipe}
          materials={materials}
          productionSheetData={selectedAssemblyForModal.productionSheet}
          type="visualInspection"
          canEdit={canEdit}
          canReport={canReport}
          canSign={canSign}
          isCustomerRepresentativeUser={isCustomerRepresentativeUser}
          isQCInspectorUser={isQCInspectorUser}
          currentUserTeamMemberId={currentUserTeamMemberId}
          isAdminOrSuperAdmin={isAdminOrSuperAdmin}
        />
      )}

      {/* Alignment Modal */}
      {isAlignmentModalOpen && (
        <FitmentVisualInspectionModal
          isOpen={isAlignmentModalOpen}
          onClose={handleCloseAlignmentModal}
          selectedItem={selectedAssemblyForModal}
          selectedPipe={selectedPipe}
          materials={materials}
          productionSheetData={selectedAssemblyForModal.productionSheet}
          type="alignment"
          canEdit={canEdit}
          canReport={canReport}
          canSign={canSign}
          isCustomerRepresentativeUser={isCustomerRepresentativeUser}
          isQCInspectorUser={isQCInspectorUser}
          currentUserTeamMemberId={currentUserTeamMemberId}
          isAdminOrSuperAdmin={isAdminOrSuperAdmin}
        />
      )}

      {/* Document Approval Modal */}
      {selectedAssemblyForApproval && (
        <DocumentApprovalModal
          isOpen={isDocumentApprovalModalOpen}
          onClose={() => {
            setIsMergeReport(false);
            setIsDocumentApprovalModalOpen(false);
            setSelectedAssemblyForApproval(null);
            // dispatch(fetchAssemblyData({
            //   projectId: selectedPipe.projectId,
            //   pipe_id: selectedPipe.pipeId
            // }));
          }}
          onOpenViewer={openViewer}
          productionSheetData={selectedAssemblyForApproval.productionSheet}
          selectedPipe={selectedPipe}
          assemblyNumber={selectedAssemblyForApproval?.assemblyNumber}
          onDownloadMergedReport={handleDownloadProductionSheetMergedReport}
          onStatusChange={(documentId, documentType, status) => {

          }}
          onRefresh={() => {
            refetchAssemblyList();
          }}
          isFitmentreport={true}
          isVisualInspectionreport={true}
          isAlignmentreport={true}
          isProductionSheetreport={true}
          isNDTreport={true}
          isCoatingreport={true}
          isWeldingreport={true}
          isMergeProductionReport={true}
          isMergeReport={isMergeReport}
          isVissibleApprovalButton={true}
          isWeldReportreport={true}
        />
      )}

      {/* PDF Viewer */}
      {viewerType === 'pdf' && viewerFile && (
        <PdfViewer
          fileUrl={viewerFile.fileUrl}
          fileName={viewerFile.fileName}
          onClose={closeViewer}
        />
      )}

      {/* Media Viewer */}
      {viewerType === 'media' && viewerFiles.length > 0 && (
        <MediaViewer
          files={viewerFiles}
          initialIndex={0}
          onClose={closeViewer}
        />
      )}

      {/* Document Viewer */}
      {viewerType === 'document' && viewerFile && (
        <DocumentViewer
          document={viewerFile}
          onClose={closeViewer}
        />
      )}

      {/* QR Code Viewer Modal */}
      <Dialog open={isQRCodeModalVisible} onOpenChange={setIsQRCodeModalVisible}>
        <DialogContent className="max-w-4xl">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                Assembly Weld QR Codes - {selectedAssemblyNumber}
              </h2>
            </div>
            {selectedAssemblyQRCodes && selectedAssemblyQRCodes.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {selectedAssemblyQRCodes.map((qrCode, index) => (
                  <div key={index} className="flex flex-col items-center justify-center space-y-4">
                    <div className="bg-white p-4 rounded-lg shadow-md border border-[#E2E5EA] w-full">
                      <div className="mb-2 text-center">
                        <label className="text-sm font-medium text-text-grey">
                          Weld {index + 1}
                        </label>
                        {/* <p className="text-xs text-heritage-grey mt-1 break-all">{qrCode.text}</p> */}
                      </div>
                      <img
                        src={qrCode.image}
                        alt={`Weld ${index + 1}: ${qrCode.text}`}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5RUiBDb2RlIE5vdCBBdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                        }}
                      />
                    </div>
                    <Button
                      // variant="outline"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = qrCode.image;
                        link.download = `assembly-${qrCode.text}-weld-${index + 1}.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        customNotification.success('Download Started', `QR code ${index + 1} is being downloaded...`);
                      }}
                      className="w-1/2 mx-auto"
                    >
                      Download Weld {index + 1}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <p className="text-heritage-grey">No QR codes available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}; 